import { useEffect, useRef, useState } from 'react';

const BOX = '[data-box-id]';

/** Whether a block placed on the open slide is at this point, under whatever covers it. */
export function boxAt(x, y) {
    if (typeof document === 'undefined') return false;
    return document.elementsFromPoint(x, y).some((el) => el.matches(BOX));
}

/** A pointer click (not Enter on a focused link) that lands on a block on the slide. */
export function clickOnBox(event) {
    return event?.detail > 0 && boxAt(event.clientX, event.clientY);
}

/**
 * Whether the mouse is over a block placed on the open slide. The previous and
 * next links span the halves of the screen above the slide, so they let the
 * pointer through while it is: a click on a block never turns the page.
 *
 * @param {boolean} enabled
 * @param {string} slideKey Changes with the slide, so a pointer left still over the new one is checked again.
 */
export function usePointerOverBox(enabled, slideKey) {
    const [over, setOver] = useState(false);
    const pointRef = useRef(null);
    const checkRef = useRef(() => {});

    useEffect(() => {
        if (!enabled || typeof document === 'undefined') {
            setOver(false);
            return undefined;
        }
        let frame = 0;
        const check = () => {
            frame = 0;
            const point = pointRef.current;
            setOver(Boolean(point) && boxAt(point.x, point.y));
        };
        checkRef.current = () => {
            if (!frame) frame = requestAnimationFrame(check);
        };
        const onMove = (event) => {
            if (event.pointerType === 'touch') return;
            pointRef.current = { x: event.clientX, y: event.clientY };
            checkRef.current();
        };
        // A press decides at once: the click that follows must already go to the block.
        const onDown = (event) => {
            onMove(event);
            if (frame) cancelAnimationFrame(frame);
            check();
        };
        // Leaving the page into a playing video's frame (inside a block) keeps the links out of
        // the way, since the page hears nothing while the pointer is over the frame.
        // Leaving the window puts the point outside the page, where there is no block.
        const root = document.documentElement;
        document.addEventListener('pointermove', onMove, { passive: true });
        document.addEventListener('pointerdown', onDown, true);
        root.addEventListener('pointerleave', onMove);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            checkRef.current = () => {};
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerdown', onDown, true);
            root.removeEventListener('pointerleave', onMove);
        };
    }, [enabled]);

    useEffect(() => {
        checkRef.current();
    }, [slideKey]);

    return over;
}

export default usePointerOverBox;
